import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import './ChatMessage.sass'
import pepWhiteLogo from '../../resources/assets/images/peperoni_white_logo.png'
import { ContentType, MessageType, ParsedMessage } from '../../types/data'
import { hexToRgbA, timestampToString } from '../../utility/utils'
import { useAppSelector } from '../../store/reducers/store'
import LinkBubble, { SmartLink } from './LinkBubble'
import Typewriter from './Typewriter'

interface Props {
    message: ParsedMessage
    onScroll: () => void
}

const ChatMessage: React.FC<Props> = (props) => {
    const data = useAppSelector((data) => data.data)
    const [isImageInvalid, setIsImageInvalid] = useState<boolean>(false)
    const [links, setLinks] = useState<SmartLink[]>([])
    const [linkVisible, setLinkVisible] = useState(false)
    const itemId = useId()

    const handleSelectionCopy = (e: any) => {
        const selection = document.getSelection()
        if (!selection) return
        e.clipboardData.setData('text/plain', selection.toString())
        e.preventDefault()
    }

    useEffect(() => {
        const messageBox = document.getElementById(itemId)
        messageBox?.addEventListener('copy', handleSelectionCopy)

        if (props.message.messageType !== MessageType.Request) {
            const smartLinks: SmartLink[] = []

            const emails = extractEmails(props.message.content)
            if (emails !== null) {
                emails.map((email) =>
                    smartLinks.push({
                        label: email,
                        link: `mailto:${email}`,
                        type: 'mail',
                    })
                )
            }

            const addresses = extractWebAddress(props.message.content)
            if (addresses !== null) {
                if (addresses !== null) {
                    addresses.map((address) => {
                        let link = address
                        if (!address.includes('https://') && !address.includes('http://')) {
                            link = `https://${address}`
                        }
                        smartLinks.push({
                            label: address,
                            link,
                            type: 'url',
                        })
                        return true
                    })
                }
            }
            const phones = extractPhone(props.message.content)
            if (phones !== null) {
                phones.map((email) =>
                    smartLinks.push({
                        label: email,
                        link: `tel:${email}`,
                        type: 'phone',
                    })
                )
            }

            setLinks(smartLinks)
        }

        return () => {
            messageBox?.removeEventListener('copy', handleSelectionCopy)
        }
    }, [])

    const extractEmails = (text: string) => {
        if (!text) {
            return null
        }
        return text.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi) as string[]
    }
    const extractWebAddress = (text: string) => {
        if (!text) {
            return null
        }
        return text.match(
            /\b((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|www\d{0,3}[.]|[a-z0-9.-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()[\]{};:'".,<>?«»“”‘’]))/gi
        ) as string[]
    }
    const extractPhone = (text: string) => {
        if (!text) {
            return null
        }
        const regexp = new RegExp(/\b(?:\+?\d{1,4}\s?)?[.-]?\(?\d{2,4}\)?[-.\s]?\d{3,5}[-.\s]?\d{2,5}\b/g)

        return text.match(regexp) as string[]
    }

    const messageComponent = useMemo(() => {
        if (props.message.contentType === ContentType.Text) {
            if (props.message.messageType === MessageType.Response) {
                return (
                    <div className="text-wrapper">
                        <Typewriter
                            text={props.message.content}
                            delay={10}
                            onFinish={() => setLinkVisible(true)}
                            onScrollTick={() => props.onScroll()}
                        />
                    </div>
                )
            }

            return <div className="text-wrapper">{props.message.content}</div>
        }

        if (isImageInvalid) {
            return (
                <div className="message-image-container message-invalid-image">
                    <img src={pepWhiteLogo} alt="Resource no longer available" />
                    <span className="invalid-image-message">Questa immagine non è più disponibile.</span>
                </div>
            )
        }

        return (
            <div className="message-image-container">
                <img
                    src={props.message.content}
                    alt="Message"
                    onError={(e) => {
                        e.preventDefault()
                        setIsImageInvalid(true)
                    }}
                />
            </div>
        )
    }, [isImageInvalid])

    const parsedTime = useMemo(() => timestampToString(props.message.datetime), [])

    const messageClassByType = useCallback((messageType: MessageType): string => {
        switch (messageType) {
            case MessageType.Response:
                return 'response'
            case MessageType.Request:
                return 'request'
            case MessageType.Loading:
                return 'loading'
            case MessageType.Error:
            default:
                return 'error'
        }
    }, [])

    return (
        <div id={itemId} className={`message-box ${messageClassByType(props.message.messageType)}-message`}>
            <div
                className="message-bubble"
                style={{
                    backgroundColor:
                        messageClassByType(props.message.messageType) === 'request'
                            ? hexToRgbA(data.currentInstance?.keys?.graphics.palette.primary, 0.8) || 'invalid'
                            : hexToRgbA(data.currentInstance?.keys?.graphics.palette.primary, 1),
                }}
            >
                <div>{messageComponent}</div>
                <div
                    className="message-footer"
                    onMouseDown={() => false}
                    onSelectCapture={() => false}
                    onSelect={() => false}
                >
                    <span className="time-indicator" data-unselectable={parsedTime} />
                </div>
            </div>

            {links !== null && linkVisible && (
                <div style={{ display: 'flex', gap: '4%', width: '100%', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {links.map((link, index) => (
                        <LinkBubble key={link.label} smartLink={link} index={index} />
                    ))}
                </div>
            )}
        </div>
    )
}

export default ChatMessage
